import {
  Box,
  Button,
  Icon,
  IconButton,
  IconProps,
  Switch,
  Text,
  useBreakpointValue,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Flex,
} from '@chakra-ui/react';

import {useMeQuery} from '@eksab/hooks';
import {Dispatch, SetStateAction, useEffect, useState} from 'react';

import {useTranslate} from '@eksab/i18n';
import {MAX_COINS, RangeSlider, MAX_ENTRY_FEE, FiltersButton} from '@eksab/components';
import {XIcon} from '@eksab/assets/icons';

export type ChallengeFilters = {
  entry_fees: [number, number];
};

type Props = {
  count: number;
  initialState: ChallengeFilters;
  onSubmit: (state: ChallengeFilters) => void;
};

export function useFiltersState() {
  const filtersState = useState(emptyState);
  return [
    ...filtersState,
    // count of filters applied
    filtersState[0].entry_fees[0] === 0 && filtersState[0].entry_fees[1] === MAX_ENTRY_FEE ? 0 : 1,
  ] as const;
}

const hideScrollBar = {
  '::-webkit-scrollbar': {
    display: 'none',
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
  },
};

const emptyState: ChallengeFilters = {
  entry_fees: [0, MAX_ENTRY_FEE],
};

useFiltersState.emptyState = emptyState;

export function Filters(props: Props) {
  const [state, setState] = useState<ChallengeFilters>({entry_fees: [0, MAX_ENTRY_FEE]});

  const placement = useBreakpointValue(['bottom', null, null, 'end']) as 'bottom' | 'end';

  const [open, setOpen] = useState(false);

  useEffect(() => {
    setState(props.initialState);
  }, [open, props.initialState]);

  const t = useTranslate();

  return (
    <>
      <FiltersButton onClick={() => setOpen(true)} open={open} count={props.count} />
      <Drawer isOpen={open} onClose={() => setOpen(false)} placement={placement} size="sm">
        <DrawerOverlay />
        <DrawerContent py="6" h={['90%', null, null, '100%']}>
          <DrawerHeader py={0} px="4">
            <Flex justify="space-between" mb="2.5">
              <Text fontWeight="600" fontSize="24">
                {t('fantasy.lineup.filters')}
              </Text>
              <IconButton
                minW="unset"
                onClick={() => setOpen(false)}
                h="unset"
                variant="unstyled"
                aria-label="Close"
                icon={<XIcon />}
              />
            </Flex>
          </DrawerHeader>
          <Body state={state} setState={setState} />
          <DrawerFooter px="4" py="0">
            <Flex mt="18px">
              <Button
                variant="unstyled"
                minW="109px"
                bgColor="white"
                borderColor="#2A2E3A"
                color="black"
                borderWidth="1px"
                me="4"
                onClick={() => {
                  props.onSubmit(emptyState);
                  setOpen(false);
                }}
              >
                {t('competitions.filters.clear')}
              </Button>
              <Button
                onClick={() => {
                  mixpanel?.track('Button Clicked', {button: 'competitions.filters.apply', filters: state});
                  props.onSubmit(state);
                  setOpen(false);
                }}
              >
                {t('competitions.filters.view_results')}
              </Button>
            </Flex>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}

const Body = ({state, setState}: {state: ChallengeFilters; setState: Dispatch<SetStateAction<ChallengeFilters>>}) => {
  const me = useMeQuery().data;
  const [coinsRange, setCoinsRange] = [
    state.entry_fees,
    (range: [number, number]) => setState((old) => ({...old, entry_fees: range})),
  ];

  const isWithinUsersRange =
    !!me &&
    ((me.coins >= MAX_COINS && MAX_ENTRY_FEE === coinsRange[1] && coinsRange[0] === 0) ||
      (coinsRange[1] === me.coins && coinsRange[0] === 0));
  const [withinUserCoinsSwitch, setwithinUserCoinsSwitch] = useState(isWithinUsersRange);

  const t = useTranslate();

  return (
    <Box flex="1" overflowY="scroll" overflowX="visible" sx={hideScrollBar} pt="2.5" px="4" id="filters-body">
      <Box borderBottom="1px" borderColor="#DFDFDF" mb="4" id="entry_fees">
        <Text fontSize="18" fontWeight="700" color="#131318" mb="4">
          {t('competition.modal.fees')}
        </Text>
        {me && (
          <Flex mb="18" justify="space-between">
            <Box>
              <Text>{t('competitions.filters.within_my_coins')}</Text>
              <Text color="#ABABAB" fontWeight="600" fontSize="14px">
                {me.coins} {t('profile.transactions.coins')}
              </Text>
            </Box>
            <Switch
              colorScheme="green"
              isChecked={withinUserCoinsSwitch && isWithinUsersRange}
              onChange={(e) => {
                if (e.target.checked) {
                  setwithinUserCoinsSwitch(true);
                  return setCoinsRange([0, me.coins > MAX_COINS ? MAX_ENTRY_FEE : me.coins]);
                }

                setwithinUserCoinsSwitch(false);
                setCoinsRange([0, MAX_ENTRY_FEE]);
              }}
            />
          </Flex>
        )}
        <RangeSlider value={coinsRange} onValueChange={setCoinsRange} />
      </Box>
    </Box>
  );
};

const FilterIcon = (props: IconProps) => (
  <Icon width="24px" height="23px" viewBox="0 0 24 23" fill="none" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.0043 22C9.65762 22 9.64768 19.6593 9.64768 15.6994C9.64768 11.7394 1.5 8.96503 1.5 4.6176C1.5 0.945201 4.75534 0.999985 11.9994 0.999985C19.2447 0.999985 22.5 0.945201 22.5 4.6176C22.5 8.96503 14.3536 11.7394 14.3536 15.6994C14.3536 19.6593 14.3498 22 12.0043 22Z"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Icon>
);
